import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { fn } from '@ember/helper';
import { eq } from 'ember-truth-helpers';
// @ts-expect-error ember-keyboard ships no type declarations
import { keyResponder as keyResponderUntyped, onKey as onKeyUntyped } from 'ember-keyboard';
import { debounce } from '@ember/runloop';
import { htmlSafe } from '@ember/template';

import type SearchService from '../services/search';
import type RouterService from '@ember/routing/router-service';
import type { SearchResult } from '../types/search';

interface CommandPaletteSignature {
  Args: {
    isOpen: boolean;
    onClose: () => void;
  };
}

// ember-keyboard ships no types; re-type its decorators locally.
const keyResponder = keyResponderUntyped as (config?: {
  priority?: number;
}) => ClassDecorator;
const onKey = onKeyUntyped as (key: string) => MethodDecorator;

@keyResponder({ priority: 2 })
export default class SearchCommandPaletteComponent extends Component<CommandPaletteSignature> {
  @service declare search: SearchService;
  @service declare router: RouterService;

  @tracked query = '';
  @tracked results: SearchResult[] = [];
  @tracked isLoading = false;
  @tracked selectedIndex = 0;
  @tracked recentSearches: string[] = [];
  @tracked hasError = false;
  @tracked errorMessage = '';

  constructor(owner: unknown, args: CommandPaletteSignature['Args']) {
    super(owner, args);
    this.loadRecentSearches();
  }

  get showRecentSearches() {
    return !this.query && this.recentSearches.length > 0;
  }

  get showResults() {
    return this.query && this.results.length > 0;
  }

  get showNoResults() {
    return this.query && !this.isLoading && this.results.length === 0 && !this.hasError;
  }

  get showError() {
    return this.hasError && this.errorMessage;
  }

  get selectedResult() {
    if (this.showResults && this.selectedIndex < this.results.length) {
      return this.results[this.selectedIndex];
    }
    return null;
  }

  get searchPlaceholder() {
    return 'Search for articles, teams, players, tournaments...';
  }

  get defaultActions() {
    return [
      { label: 'Home', route: 'index', icon: '🏠' },
      { label: 'eSports', route: 'esports', icon: '🏆' },
      { label: 'News', route: 'news', icon: '📰' },
    ];
  }

  // Keyboard navigation
  @onKey('Escape')
  @action
  closeModal() {
    this.args.onClose();
    this.resetState();
  }

  @onKey('Enter')
  @action
  selectResult() {
    if (this.showRecentSearches && this.selectedIndex < this.recentSearches.length) {
      // Select from recent searches
      const recentQuery = this.recentSearches[this.selectedIndex];
      this.query = recentQuery;
      this.performSearch(recentQuery);
    } else if (this.selectedResult) {
      // Navigate to selected result
      this.navigateToResult(this.selectedResult);
    }
  }

  @onKey('ArrowDown')
  @action
  navigateDown() {
    const maxIndex = this.showResults ? this.results.length - 1 : this.recentSearches.length - 1;

    if (this.selectedIndex < maxIndex) {
      this.selectedIndex++;
    }
  }

  @onKey('ArrowUp')
  @action
  navigateUp() {
    if (this.selectedIndex > 0) {
      this.selectedIndex--;
    }
  }

  @action
  handleInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.query = input.value;
    this.selectedIndex = 0; // Reset selection

    if (this.query.trim()) {
      this.debouncedSearch(this.query.trim());
    } else {
      this.results = [];
      this.hasError = false;
      this.isLoading = false;
    }
  }

  @action
  debouncedSearch(query: string) {
    debounce(this, this.performSearch, query, 300);
  }

  @action
  async performSearch(query: string) {
    if (!query.trim()) return;

    this.isLoading = true;
    this.hasError = false;

    try {
      const searchResponse = await this.search.search(
        {
          query: query.trim(),
          page: 1,
          limit: 15, // More results for better command palette experience
          includeHighlights: true,
          includeSuggestions: false, // Don't need suggestions in command palette
        },
        {
          enableAnalytics: true,
          enableCache: true,
        },
      );

      this.results = searchResponse.results || [];

      // Add to recent searches if we got results
      if (this.results.length > 0) {
        this.addToRecentSearches(query);
      }
    } catch (error) {
      console.error('Command palette search error:', error);
      this.hasError = true;
      this.errorMessage = 'Search failed. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  @action
  navigateToResult(result: SearchResult) {
    // Track the click
    this.search.trackResultClick(this.query, result, this.selectedIndex);

    // Navigate to the result
    window.location.href = result.url;

    // Close modal
    this.args.onClose();
    this.resetState();
  }

  @action
  handleResultClick(result: SearchResult, index: number) {
    this.selectedIndex = index;
    this.navigateToResult(result);
  }

  @action
  handleRecentSearchClick(query: string) {
    this.query = query;
    this.performSearch(query);
  }

  @action
  clearRecentSearches() {
    this.recentSearches = [];
    this.saveRecentSearches();
  }

  @action
  handleModalBackgroundClick(event: Event) {
    // Close modal if clicking on background, not content
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  @action
  setSelectedIndex(index: number) {
    this.selectedIndex = index;
  }

  @action
  clearQuery() {
    this.query = '';
  }

  @action
  handleDefaultAction(action: { label: string; route: string; icon: string }) {
    this.router.transitionTo(action.route);
    this.closeModal();
  }

  private resetState() {
    this.query = '';
    this.results = [];
    this.selectedIndex = 0;
    this.isLoading = false;
    this.hasError = false;
    this.errorMessage = '';
  }

  private loadRecentSearches() {
    this.recentSearches = this.search.getRecentSearches();
  }

  private addToRecentSearches(query: string) {
    // Remove if already exists
    this.recentSearches = this.recentSearches.filter((search) => search !== query);

    // Add to beginning
    this.recentSearches.unshift(query);

    // Keep only last 5 for command palette
    this.recentSearches = this.recentSearches.slice(0, 5);

    // Save to localStorage
    this.saveRecentSearches();
  }

  private saveRecentSearches() {
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('1vx-recent-searches', JSON.stringify(this.recentSearches));
      } catch (error) {
        console.warn('Error saving recent searches:', error);
      }
    }
  }

  // Helper methods for styling
  getResultIcon(type: string) {
    const iconMap: Record<string, string> = {
      article: '📄',
      team: '👥',
      player: '🎮',
      tournament: '🏆',
    };
    return iconMap[type] || '📄';
  }

  getResultTypeLabel(type: string) {
    const labelMap: Record<string, string> = {
      article: 'Article',
      team: 'Team',
      player: 'Player',
      tournament: 'Tournament',
    };
    return labelMap[type] || 'Content';
  }

  getHighlightedTitle(result: SearchResult): string {
    return result.highlightedFields?.title || result.title || '';
  }

  getHighlightedExcerpt(result: SearchResult): string {
    return result.highlightedFields?.excerpt || result.excerpt || '';
  }

  <template>
    {{#if @isOpen}}
      <div class="command-palette-overlay" {{on "click" this.handleModalBackgroundClick}}>
        <div class="command-palette">
          {{! Search Input }}
          <div class="command-palette-header">
            <div class="search-input-wrapper">
              <svg class="search-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
              </svg>
              <input
                type="text"
                class="search-input"
                placeholder={{this.searchPlaceholder}}
                value={{this.query}}
                {{on "input" this.handleInputChange}}
                autofocus
              />
              {{#if this.query}}
                <button type="button" class="clear-button" {{on "click" this.clearQuery}}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              {{/if}}
            </div>
          </div>

          {{! Content Area }}
          <div class="command-palette-content">
            {{#if this.isLoading}}
              {{! Loading State }}
              <div class="command-palette-loading">
                <div class="loading-spinner"></div>
                <p>Searching...</p>
              </div>

            {{else if this.showError}}
              {{! Error State }}
              <div class="command-palette-error">
                <svg class="error-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
                <p>{{this.errorMessage}}</p>
              </div>

            {{else if this.showResults}}
              {{! Search Results }}
              <div class="command-palette-section">
                <h3 class="section-title">Search Results</h3>
                <div class="results-list">
                  {{#each this.results as |result index|}}
                    <button
                      type="button"
                      class="result-item {{if (eq index this.selectedIndex) 'selected'}}"
                      {{on "click" (fn this.handleResultClick result index)}}
                      {{on "mouseenter" (fn this.setSelectedIndex index)}}
                    >
                      <div class="result-icon">{{this.getResultIcon result.type}}</div>
                      <div class="result-content">
                        <div class="result-title">
                          {{{this.getHighlightedTitle result}}}
                        </div>
                        {{#if result.excerpt}}
                          <div class="result-excerpt">
                            {{{this.getHighlightedExcerpt result}}}
                          </div>
                        {{/if}}
                        <div class="result-meta">
                          <span class="result-type">{{this.getResultTypeLabel result.type}}</span>
                          {{#if result.author}}
                            <span class="result-separator">•</span>
                            <span class="result-author">{{result.author}}</span>
                          {{/if}}
                        </div>
                      </div>
                      <div class="result-arrow">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                        </svg>
                      </div>
                    </button>
                  {{/each}}
                </div>
              </div>

            {{else if this.showNoResults}}
              {{! No Results }}
              <div class="command-palette-empty">
                <svg class="empty-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                </svg>
                <p>No results found for "{{this.query}}"</p>
                <p class="empty-suggestion">Try a different search term</p>
              </div>

            {{else}}
              {{! Default Actions }}
              <div class="command-palette-section">
                <h3 class="section-title">Quick Actions</h3>
                <div class="results-list">
                  {{#each this.defaultActions as |action|}}
                    <button
                      type="button"
                      class="result-item"
                      {{on "click" (fn this.handleDefaultAction action)}}
                    >
                      <div class="result-icon">{{action.icon}}</div>
                      <div class="result-content">
                        <div class="result-title">{{action.label}}</div>
                      </div>
                      <div class="result-arrow">
                        <svg viewBox="0 0 20 20" fill="currentColor">
                          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                        </svg>
                      </div>
                    </button>
                  {{/each}}
                </div>
              </div>

              {{#if this.showRecentSearches}}
                {{! Recent Searches }}
                <div class="command-palette-section">
                  <div class="section-header">
                    <h3 class="section-title">Recent Searches</h3>
                    <button type="button" class="clear-recents-button" {{on "click" this.clearRecentSearches}}>
                      Clear
                    </button>
                  </div>
                  <div class="results-list">
                    {{#each this.recentSearches as |query index|}}
                      <button
                        type="button"
                        class="result-item recent-search {{if (eq index this.selectedIndex) 'selected'}}"
                        {{on "click" (fn this.handleRecentSearchClick query)}}
                        {{on "mouseenter" (fn this.setSelectedIndex index)}}
                      >
                        <div class="result-icon">🕒</div>
                        <div class="result-content">
                          <div class="result-title">{{query}}</div>
                        </div>
                        <div class="result-arrow">
                          <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                          </svg>
                        </div>
                      </button>
                    {{/each}}
                  </div>
                </div>
              {{/if}}
            {{/if}}
          </div>

          {{! Footer with shortcuts }}
          <div class="command-palette-footer">
            <div class="shortcuts">
              <kbd class="shortcut">↑↓</kbd> to navigate
              <kbd class="shortcut">↵</kbd> to select
              <kbd class="shortcut">esc</kbd> to close
            </div>
          </div>
        </div>
      </div>
    {{/if}}
  </template>
}
